import { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { api, AUTH_EXPIRED_EVENT } from '../services/api'
import toast from 'react-hot-toast'
import { useOpinly } from '@opinly/react/pixel'

const AuthContext = createContext()

export function AuthProvider({ children }) {
    const opinlyAnalytics = useOpinly()
    const [user, setUser] = useState(null)
    const [loading, setLoading] = useState(true)
    const [socket, setSocket] = useState(null)
    const [totalUnread, setTotalUnread] = useState(0)

    const checkUser = async () => {
        try {
            const data = await api.auth.getMe();
            setUser(data.user);
        } catch (error) {
            setUser(null);
        } finally {
            setLoading(false);
        }
    };

    const refreshUnread = useCallback(async () => {
        try {
            const list = await api.conversations.list();
            const sum = list.reduce((acc, c) => acc + (c.unread_count || 0), 0);
            setTotalUnread(sum);
        } catch {
            // Non-fatal — ignore (likely unauthenticated or offline).
        }
    }, []);

    useEffect(() => {
        checkUser()
    }, [])

    useEffect(() => {
        if (user?.email) {
            opinlyAnalytics.identify({ email: user.email, userId: String(user.id) })
        }
    }, [opinlyAnalytics, user?.email, user?.id])

    useEffect(() => {
        const clearExpiredSession = () => {
            setUser(null);
            setTotalUnread(0);
        };
        window.addEventListener(AUTH_EXPIRED_EVENT, clearExpiredSession);
        return () => window.removeEventListener(AUTH_EXPIRED_EVENT, clearExpiredSession);
    }, [])

    useEffect(() => {
        if (!user) {
            setTotalUnread(0);
            return;
        }
        refreshUnread();
        let active = true;
        let cleanupSocket = null;

        (async () => {
            const { io } = await import('socket.io-client');
            if (!active) return;

            const socketUrl = typeof window !== 'undefined' ? window.location.origin : 'http://localhost:3000';
            const newSocket = io(socketUrl, { withCredentials: true })

            newSocket.on('connect', () => {
                newSocket.emit('join', user.id)
            })

            newSocket.on('notification', (data) => {
                if (data.link) {
                    toast((t) => (
                        <div
                            onClick={() => { toast.dismiss(t.id); window.location.href = data.link; }}
                            className="cursor-pointer text-sm"
                        >
                            {data.message}
                            <span className="block text-xs text-blue-500 mt-0.5">Tap to open →</span>
                        </div>
                    ), { duration: 6000, icon: '✅' });
                } else {
                    toast.success(data.message, { duration: 5000 });
                }
            })

            newSocket.on('chat:message', ({ conversationId, message }) => {
                window.dispatchEvent(new CustomEvent('chat:message', { detail: { conversationId, message } }));
                if (message.sender_id !== user.id) {
                    refreshUnread();
                }
            })

            newSocket.on('chat:new-conversation', ({ conversationId }) => {
                window.dispatchEvent(new CustomEvent('chat:new-conversation', { detail: { conversationId } }));
            })

            newSocket.on('support:message', ({ chatId, message }) => {
                window.dispatchEvent(new CustomEvent('support:message', { detail: { chatId, message } }));
            })

            newSocket.on('chat:read', ({ conversationId, readerId }) => {
                window.dispatchEvent(new CustomEvent('chat:read', { detail: { conversationId, readerId } }));
                refreshUnread();
            })

            setSocket(newSocket)
            cleanupSocket = () => newSocket.close()
        })()

        return () => {
            active = false;
            if (cleanupSocket) cleanupSocket();
        }
    }, [user, refreshUnread])

    const login = async (credentials) => {
        const data = await api.auth.login(credentials);
        setUser(data.user);
        opinlyAnalytics.track('login', { method: 'email' });
        return data;
    };

    const register = async (userData) => {
        const data = await api.auth.register(userData);
        setUser(data.user);
        opinlyAnalytics.track('sign_up', { method: 'email', role: data.user.role }, {
            externalEventId: `sign_up:${data.user.id}`,
        });
        return data;
    };

    const logout = async () => {
        if (socket) {
            socket.disconnect();
            setSocket(null);
        }
        await api.auth.logout();
        setUser(null);
    };

    return (
        <AuthContext.Provider value={{ user, login, register, logout, loading, setUser, totalUnread, setTotalUnread, refreshUnread }}>
            {(typeof window === 'undefined' || !loading) && children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
